import api from '@/lib/api/crate-core-api-client'
import { ATTRIBUTION_ENABLED } from '@/lib/config'
import { AttributionEndpointKind, AttributionEndpointResolution, TouchpointPayload } from '@/lib/attribution/types'
import {
  getAnonymousId,
  getAttributionConsent,
  getCurrentPageUrl,
  getLandingPage,
  getSessionId,
  getSessionMarketingParams,
  getSessionReferrer,
} from '@/lib/attribution/storage'
import { attributionDebug, attributionWarn } from '@/lib/attribution/logger'

const endpointPaths: Record<AttributionEndpointKind, string> = {
  touchpoint: '/attribution/touchpoint',
  login: '/auth/login',
  register: '/auth/register',
}

function truncateValue(value: string, maxLength = 2048): string {
  return value.length > maxLength ? value.slice(0, maxLength) : value
}

export function resolveAttributionEndpoint(kind: AttributionEndpointKind): AttributionEndpointResolution {
  const primary = endpointPaths[kind]

  attributionDebug('Resolved attribution endpoint', {
    kind,
    attributionEnabled: ATTRIBUTION_ENABLED,
    primary,
  })

  return { primary }
}

export async function trackTouchpoint(eventType = 'pageview', metadata?: Record<string, unknown>): Promise<void> {
  if (typeof window === 'undefined') return

  if (!ATTRIBUTION_ENABLED) {
    attributionDebug('Skipping touchpoint tracking because attribution is disabled', {
      eventType,
    })
    return
  }

  const anonymousId = getAnonymousId()
  const sessionId = getSessionId()
  const pageUrl = getCurrentPageUrl()
  const landingPage = getLandingPage()

  if (!anonymousId || !sessionId || !pageUrl || !landingPage) {
    attributionDebug('Skipping touchpoint tracking due to missing required fields', {
      hasAnonymousId: !!anonymousId,
      hasSessionId: !!sessionId,
      hasPageUrl: !!pageUrl,
      hasLandingPage: !!landingPage,
      eventType,
    })
    return
  }

  const consentState = getAttributionConsent()
  const consentGiven = consentState === 'unknown' ? undefined : consentState === 'granted'
  const marketingParams = getSessionMarketingParams()
  const referrerUrl = getSessionReferrer()

  const payload: TouchpointPayload = {
    anonymous_id: anonymousId,
    session_id: sessionId,
    ...marketingParams,
    referrer_url: referrerUrl ? truncateValue(referrerUrl) : undefined,
    landing_page: truncateValue(landingPage),
    page_url: truncateValue(pageUrl),
    event_type: eventType,
    consent_given: consentGiven,
    metadata,
  }

  const endpoint = resolveAttributionEndpoint('touchpoint')
  attributionDebug('Sending touchpoint request', {
    eventType,
    endpoint: endpoint.primary,
    payload,
  })

  try {
    await api.post(endpoint.primary, payload)
    attributionDebug('Touchpoint request succeeded', {
      endpoint: endpoint.primary,
      eventType,
    })
  } catch {
    // Fail silently by design.
    attributionWarn('Touchpoint request failed', {
      endpoint: endpoint.primary,
      eventType,
    })
  }
}
